// Mapa stanic na podkladu OSM (OpenLayers): vector features, toolbar (vizualizace + legenda + popis + hint), click + hover na stanici/mapu
import { useEffect, useMemo, useRef, useState } from 'react'
import Map from 'ol/Map'
import { defaults as defaultControls, Zoom } from 'ol/control'
import View from 'ol/View'
import TileLayer from 'ol/layer/Tile'
import OSM from 'ol/source/OSM'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import Feature from 'ol/Feature'
import Point from 'ol/geom/Point'
import { fromLonLat, toLonLat } from 'ol/proj'
import { Select as OLSelect } from 'ol/interaction'
import { click } from 'ol/events/condition'
import Style from 'ol/style/Style'
import Icon from 'ol/style/Icon'
import Text from 'ol/style/Text'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Circle from 'ol/style/Circle'
import 'ol/ol.css'

import { MAP_CONFIG } from '../../config/mapConfig'
import { makeFeatureStyle, stationLabel, LEGEND, buildTypeColors, buildTypeLegend, elevationColor, EL_COLORS, EL_PRIORITY, HLY_EL_PRIORITY, MIN10_EL_PRIORITY, getDlyElements, getHlyElements, get10mElements, OBS_TYPE_COLORS, OBS_TYPE_PRIORITY, getObsTypes } from './featureStyles'
import { StationPanelHint } from '../panels/StationPanelHint'
import { useStationStore } from '../../store/stationStore'
import { useStationTypes } from '../../hooks/useAppData'

// Popisek markeru na mapě: null když labelMode neobsahuje žádné pole nebo stanice nemá hodnotu.
// labelStyle — volitelné přepsání vzhledu (font/barva), viz prop StationMap.labelStyle
// (zjednodušená mapa /station-charts chce výraznější popisky než dnešní výchozí styl).
const makeLabelText = (label, offsetY, labelStyle = null) => label ? new Text({
  text: label,
  offsetY,
  font: labelStyle?.font ?? '10px sans-serif',
  fill: new Fill({ color: labelStyle?.color ?? '#333' }),
  stroke: new Stroke({ color: '#fff', width: labelStyle?.strokeWidth ?? 2 }),
}) : null

const VIZ_MODES = [
  { value: 'elevation', label: 'Výška (nadm.)' },
  { value: 'type-color', label: 'Typ (barva 1)' },
  { value: 'type-color-alpha', label: 'Typ (barva 2)' },
  { value: 'type-color-freq', label: 'Typ (barva 3)' },
  { value: 'type-color-hsl', label: 'Typ (barva 4)' },
  { value: 'type-shape', label: 'Pozorování (koláč)' },
  { value: 'obs-color', label: 'Prvky DLY (barva)' },
  { value: 'obs-color-hly', label: 'Prvky HLY (barva)' },
  { value: 'obs-color-10m', label: 'Prvky 10M (barva)' },
]

// Mapování vizMode → strategie buildTypeColors (viz featureStyles.js): barva 1 = natvrdo
// z DB sloupce station_type.color, barva 2 = stabilní hash id (abecedně-nezávislé), barva 3 =
// podle četnosti (nejčastější typ dostane klidnou barvu), barva 4 = rovnoměrný barevný kruh
// (žádná barva vizuálně nedominuje).
const TYPE_COLOR_VARIANTS = {
  'type-color': 'db',
  'type-color-alpha': 'alpha',
  'type-color-freq': 'freq',
  'type-color-hsl': 'hsl',
}

// Prepend „Podle skupiny“ jako preferovaný/první mód — buď natrvalo (groupColorMode, StationGroupLayout,
// viz sekce III plánu), nebo jen jako doplňková volba (groupVizAvailable, StationSelectLayout — vidět
// jen když je ve filtru „Skupina“ vybraný aspoň jeden uzel)
const GROUP_VIZ_MODE = { value: 'group', label: 'Podle skupiny' }

// Kombinovatelné popisky (checkboxy) — vybraná pole se na mapě zobrazí spojená čárkou
// (viz stationLabel ve featureStyles.js), např. Název + Upřesnění → "Praha, Klementinum".
const LABEL_MODES = [
  { value: 'id', label: 'ID stanice' },
  { value: 'name', label: 'Název stanice' },
  { value: 'qual', label: 'Upřesnění' },
  { value: 'full_name', label: 'Plný název' },
  { value: 'wsi', label: 'WSI' },
]

// === MAPA KONFIGURACE ===
const MAP_CONFIG_OFFSET = {
  ZOOM_THRESHOLD: 14,              // Zoom level pro přepnutí cluster/offset
  LABEL_ZOOM_THRESHOLD: 8,         // Popisky stanic se zobrazí až od tohoto zoomu (níž jsou příliš husté a překrývají se)
  PROXIMITY_DISTANCE_METERS: 25,    // Seskupi stanice v okruhu X metrů
  OFFSET_RADIUS_KM: 0.1,           // Radius offsetu markerů v km
  CLUSTER_ZOOM_INCREMENT: 3,       // Kolik levelů přiblížit na klik na cluster
  CLUSTER_ANIMATION_DURATION: 300, // Trvání animace zoolu v ms
  CLUSTER_CIRCLE_RADIUS: 10,       // Pixel radius clusteru
  CLUSTER_FILL_COLOR: 'rgba(51, 122, 183, 0.8)',
  CLUSTER_STROKE_COLOR: '#fff',
  CLUSTER_STROKE_WIDTH: 2,
  CLUSTER_TEXT_COLOR: '#fff',
  CLUSTER_TEXT_FONT: 'bold 12px Arial',
}

// Posun markeru (offsetování) — stanice se stejnými souř. se zobrazí v kruhu
// radiusKm = vzdálenost od středu (výchozí 0.3 km)
function getOffsetCoordinates(lon, lat, index, totalCount, radiusKm = MAP_CONFIG_OFFSET.OFFSET_RADIUS_KM) {
  if (totalCount <= 1) return [lon, lat]

  const angleStep = (Math.PI * 2) / totalCount
  const angle = angleStep * index

  // Aproximace: 1 stupeň ≈ 111 km
  const radiusDeg = radiusKm / 111
  const offsetLon = lon + radiusDeg * Math.cos(angle)
  const offsetLat = lat + radiusDeg * Math.sin(angle)

  return [offsetLon, offsetLat]
}

// Seskupi stanice podle souřadnic
function groupStationsByCoordinates(stations) {
  const grouped = {}
  stations.forEach(s => {
    if (s.latitude != null && s.longitude != null) {
      const key = `${s.longitude},${s.latitude}`
      if (!grouped[key]) grouped[key] = []
      grouped[key].push(s)
    }
  })
  return grouped
}

// Seskupi stanice které jsou blízko sebe (v okruhu distanceMeters)
function groupStationsByProximity(stations, distanceMeters = 5) {
  const groups = []
  const processed = new Set()

  stations.forEach((station, idx) => {
    if (processed.has(idx)) return
    if (station.latitude == null || station.longitude == null) return

    const group = [station]
    processed.add(idx)

    const latRad = station.latitude * Math.PI / 180

    stations.forEach((otherStation, otherIdx) => {
      if (processed.has(otherIdx) || otherIdx === idx) return
      if (otherStation.latitude == null || otherStation.longitude == null) return

      const latMeters = Math.abs(station.latitude - otherStation.latitude) * 111000
      const lonMeters = Math.abs(station.longitude - otherStation.longitude) * 111000 * Math.cos(latRad)
      const distance = Math.sqrt(latMeters ** 2 + lonMeters ** 2)

      if (distance < distanceMeters) {
        group.push(otherStation)
        processed.add(otherIdx)
      }
    })

    groups.push(group)
  })

  return groups
}

// Konfigurace pro "Prvky <obs_type> (barva)" módy — sdílená mezi cluster koláčem a jednotlivou stanicí
const OBS_COLOR_MODES = {
  'obs-color':     { getElements: getDlyElements, priority: EL_PRIORITY },
  'obs-color-hly': { getElements: getHlyElements, priority: HLY_EL_PRIORITY },
  'obs-color-10m': { getElements: get10mElements, priority: MIN10_EL_PRIORITY },
}

// Vrátí barvu výseče pro danou stanici podle vizMode
function clusterSliceColor(station, vizMode, typeColors, groupColorMap) {
  switch (vizMode) {
    case 'elevation':
      return elevationColor(station.elevation)
    case 'obs-color':
    case 'obs-color-hly':
    case 'obs-color-10m':
      return '#607d8b'
    case 'type-shape':
      return '#2196f3'
    case 'type-color':
    case 'type-color-alpha':
    case 'type-color-freq':
    case 'type-color-hsl':
    default:
      return typeColors[station.station_type] || '#999'
  }
}

// Vytvoř pie chart pro cluster — výseče obarveny podle vizMode
function createClusterPieChart(clusterStations, vizMode, typeColors, groupColorMap, radius = 20) {
  const canvas = document.createElement('canvas')
  const size = radius * 2 + 4
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')

  const total = clusterStations.length
  const sliceAngle = (Math.PI * 2) / total
  const centerX = size / 2
  const centerY = size / 2
  let startAngle = -Math.PI / 2

  clusterStations.forEach(station => {
    ctx.fillStyle = clusterSliceColor(station, vizMode, typeColors, groupColorMap)
    ctx.beginPath()
    ctx.moveTo(centerX, centerY)
    ctx.arc(centerX, centerY, radius, startAngle, startAngle + sliceAngle)
    ctx.closePath()
    ctx.fill()
    startAngle += sliceAngle
  })

  // Bílý border
  ctx.strokeStyle = '#fff'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2)
  ctx.stroke()

  // Počet uprostřed
  ctx.fillStyle = '#fff'
  ctx.font = 'bold 12px Arial'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(String(total), centerX, centerY)

  return canvas
}

// Vytvoř hexagon pro cluster v type-shape módu
// Koláčový graf jedné stanice – items = seřazené klíče, colorMap = klíč→barva
function createPieChart(items, colorMap, radius = 9) {
  const canvas = document.createElement('canvas')
  const size = radius * 2 + 4
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  const cx = size / 2
  const cy = size / 2

  if (items.length === 0) {
    ctx.beginPath()
    ctx.arc(cx, cy, radius, 0, Math.PI * 2)
    ctx.fillStyle = '#aaa'
    ctx.fill()
  } else if (items.length === 1) {
    ctx.beginPath()
    ctx.arc(cx, cy, radius, 0, Math.PI * 2)
    ctx.fillStyle = colorMap[items[0]]
    ctx.fill()
  } else {
    const sliceAngle = (Math.PI * 2) / items.length
    let startAngle = -Math.PI / 2
    items.forEach(key => {
      ctx.fillStyle = colorMap[key]
      ctx.beginPath()
      ctx.moveTo(cx, cy)
      ctx.arc(cx, cy, radius, startAngle, startAngle + sliceAngle)
      ctx.closePath()
      ctx.fill()
      startAngle += sliceAngle
    })
  }

  ctx.strokeStyle = '#fff'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.arc(cx, cy, radius, 0, Math.PI * 2)
  ctx.stroke()

  return canvas
}

// slices = [{ color, weight }, ...] – proporcionální koláč s počtem uprostřed
function createClusterPieWithCount(slices, count, radius = 9) {
  const canvas = document.createElement('canvas')
  const size = radius * 2 + 4
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  const cx = size / 2
  const cy = size / 2

  const total = slices.reduce((s, sl) => s + sl.weight, 0)

  if (slices.length === 0 || total === 0) {
    ctx.beginPath()
    ctx.arc(cx, cy, radius, 0, Math.PI * 2)
    ctx.fillStyle = '#aaa'
    ctx.fill()
  } else if (slices.length === 1) {
    ctx.beginPath()
    ctx.arc(cx, cy, radius, 0, Math.PI * 2)
    ctx.fillStyle = slices[0].color
    ctx.fill()
  } else {
    let startAngle = -Math.PI / 2
    slices.forEach(sl => {
      const angle = (sl.weight / total) * Math.PI * 2
      ctx.fillStyle = sl.color
      ctx.beginPath()
      ctx.moveTo(cx, cy)
      ctx.arc(cx, cy, radius, startAngle, startAngle + angle)
      ctx.closePath()
      ctx.fill()
      startAngle += angle
    })
  }

  ctx.strokeStyle = '#fff'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.arc(cx, cy, radius, 0, Math.PI * 2)
  ctx.stroke()

  ctx.fillStyle = '#fff'
  ctx.font = 'bold 10px Arial'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(String(count), cx, cy)

  return canvas
}

function getClusterElSlices(clusterStations, getElements, priority) {
  const counts = {}
  clusterStations.forEach(station => {
    getElements(station.observation).forEach(el => {
      counts[el] = (counts[el] || 0) + 1
    })
  })
  return priority.filter(el => counts[el]).map(el => ({ color: EL_COLORS[el], weight: counts[el] }))
}

function getClusterObsTypeSlices(clusterStations) {
  const counts = {}
  clusterStations.forEach(station => {
    getObsTypes(station.observation).forEach(t => {
      counts[t] = (counts[t] || 0) + 1
    })
  })
  return OBS_TYPE_PRIORITY.filter(t => counts[t]).map(t => ({ color: OBS_TYPE_COLORS[t], weight: counts[t] }))
}

// Sloučení barev skupin všech stanic v clusteru (včetně stanic s víc barvami při překryvu) do jednoho
// proporcionálního koláče — stejný princip jako getClusterElSlices/getClusterObsTypeSlices.
function getClusterGroupSlices(clusterStations, groupColorMap) {
  const counts = {}
  clusterStations.forEach(station => {
    const colors = (groupColorMap && groupColorMap.get(station.gh_id)) || []
    colors.forEach(color => {
      counts[color] = (counts[color] || 0) + 1
    })
  })
  return Object.entries(counts).map(([color, weight]) => ({ color, weight }))
}

export function StationMap({ stations, selectedStation, onStationSelect, onMapClick, groupColorMode = false, groupVizAvailable = false, groupColorMap = null, groupLegend = null, localityPickMode = false, hideTools = false, pointRadius = null, fixedLabelMode = null, labelStyle = null, defaultShowLegend = false, onLegendVisibilityChange = null }) {
  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const vectorSourceRef = useRef(null)
  const vectorLayerRef = useRef(null)
  const mapStateRef = useRef(null)
  const localityPickModeRef = useRef(localityPickMode)
  useEffect(() => { localityPickModeRef.current = localityPickMode }, [localityPickMode])

  const stationTypes = useStationTypes()

  const mapState = useStationStore((state) => state.mapState)
  const setMapState = useStationStore((state) => state.setMapState)
  const mapSettings = useStationStore((state) => state.mapSettings)
  const setMapSettings = useStationStore((state) => state.setMapSettings)

  // Keep latest callbacks in refs to avoid stale closures in OL event handlers
  const onStationSelectRef = useRef(onStationSelect)
  const onMapClickRef = useRef(onMapClick)
  useEffect(() => { onStationSelectRef.current = onStationSelect }, [onStationSelect])
  useEffect(() => { onMapClickRef.current = onMapClick }, [onMapClick])

  const [vizMode, setVizMode] = useState(groupColorMode ? 'group' : mapSettings.vizMode)
  // labelMode: pole kombinovatelných polí (viz LABEL_MODES) — fixedLabelMode (např.
  // /station-charts chce natvrdo ['name', 'qual']) přebíjí uloženého uživatelské nastavení.
  const [labelMode, setLabelModeState] = useState(fixedLabelMode ?? mapSettings.labelMode)
  const toggleLabelMode = (value) => setLabelModeState(prev =>
    prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
  )

  const typeCounts = useMemo(() => {
    const counts = {}
    ;(stations || []).forEach(s => {
      if (s.station_type) counts[s.station_type] = (counts[s.station_type] || 0) + 1
    })
    return counts
  }, [stations])
  const typeColorVariant = TYPE_COLOR_VARIANTS[vizMode] || 'db'
  const typeColors = useMemo(
    () => buildTypeColors(
      typeColorVariant === 'db' ? stationTypes : stationTypes.map(t => t.id),
      typeCounts,
      typeColorVariant
    ),
    [stationTypes, typeCounts, typeColorVariant]
  )
  const [showToolbar, setShowToolbar] = useState(hideTools ? false : mapSettings.showToolbar)
  const [showLegend, setShowLegendState] = useState(hideTools ? defaultShowLegend : mapSettings.showLegend)
  // hideTools mapy (/station-charts) neukládají do sdíleného mapSettings (viz níže) — persistenci
  // zavřené/otevřené legendy řeší volající přes onLegendVisibilityChange, pokud ji chce pamatovat.
  const setShowLegend = (value) => {
    setShowLegendState(prev => {
      const next = typeof value === 'function' ? value(prev) : value
      if (hideTools) onLegendVisibilityChange?.(next)
      return next
    })
  }
  const [showHint, setShowHint] = useState(hideTools ? false : mapSettings.showHint)
  const [hintStation, setHintStation] = useState(null)
  const [hintPos, setHintPos] = useState({ x: 0, y: 0 })
  const [clusterSelectionActive, setClusterSelectionActive] = useState(false)
  const [zoomLevel, setZoomLevel] = useState(mapState?.zoom ?? 7)
  const showHintRef = useRef(true)
  const clusterSelectionActiveRef = useRef(false)
  const hintPanelRef = useRef(null)
  useEffect(() => { showHintRef.current = showHint }, [showHint])
  useEffect(() => {
    clusterSelectionActiveRef.current = clusterSelectionActive
  }, [clusterSelectionActive])

  // Update cursor immediately when locality pick mode toggles (don't wait for pointermove)
  useEffect(() => {
    const target = mapInstanceRef.current?.getTargetElement()
    if (target) target.style.cursor = localityPickMode ? 'crosshair' : ''
  }, [localityPickMode])

  // Close cluster selection on Escape or click outside
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && clusterSelectionActive) {
        setClusterSelectionActive(false)
      }
    }
    const handleClickOutside = (e) => {
      if (clusterSelectionActive && hintPanelRef.current) {
        // Only close if panel exists and click is actually outside
        if (!hintPanelRef.current.contains(e.target)) {
          setClusterSelectionActive(false)
        }
      }
    }
    if (clusterSelectionActive) {
      // Delay click handler to avoid catching the click that opened the selection
      setTimeout(() => {
        document.addEventListener('keydown', handleKeyDown)
        document.addEventListener('click', handleClickOutside)
      }, 100)
      return () => {
        document.removeEventListener('keydown', handleKeyDown)
        document.removeEventListener('click', handleClickOutside)
      }
    }
  }, [clusterSelectionActive])

  // Ulož mapSettings do store (skip pro groupColorMode — 'group' vizMode dává smysl jen ve StationGroupLayout,
  // kde je mapa natrvalo v tomhle módu; groupVizAvailable je jen doplňková volba v běžné mapě, ukládá se normálně.
  // hideTools — zjednodušená mapa /station-charts — nemá vlastní přepínače, nemá smysl ukládat.)
  useEffect(() => {
    if (groupColorMode || hideTools) return
    setMapSettings({ vizMode, labelMode, showToolbar, showLegend, showHint })
  }, [vizMode, labelMode, showToolbar, showLegend, showHint, setMapSettings, groupColorMode, hideTools])

  // Init map once
  useEffect(() => {
    if (mapInstanceRef.current) return

    const vectorSource = new VectorSource()
    vectorSourceRef.current = vectorSource

    const vectorLayer = new VectorLayer({
      source: vectorSource,
      renderOrder: (a, b) => (b.get('ghId') ?? '').localeCompare(a.get('ghId') ?? ''),
    })
    vectorLayerRef.current = vectorLayer

    // Použij uložený stav mapy nebo default
    const center = mapState?.center || MAP_CONFIG.center
    const zoom = mapState?.zoom ?? MAP_CONFIG.zoom

    const map = new Map({
      target: mapRef.current,
      controls: defaultControls({ zoom: false }).extend([
        new Zoom({ className: 'ol-zoom ol-zoom-topright' }),
      ]),
      layers: [
        new TileLayer({ source: new OSM() }),
        vectorLayer,
      ],
      view: new View({
        center: fromLonLat(center),
        zoom: zoom,
        // Sever vždy nahoru — mapu nejde otočit (rotace vypnutá napevno, i pro touch pinch-rotate gesto).
        enableRotation: false,
      }),
    })
    mapInstanceRef.current = map
    mapStateRef.current = map.getView()

    // Feature click via Select interaction — hitTolerance zvětšuje klikatelnou plochu kolem bodu
    // (výchozí 0 = pixel-perfect zásah, na touch zařízeních prakticky nemožné trefit malé kolečko).
    const selectInteraction = new OLSelect({ condition: click, hitTolerance: 12 })
    selectInteraction.on('select', (e) => {
      if (e.selected.length > 0) {
        const feature = e.selected[0]
        const isCluster = feature.get('isCluster')

        if (isCluster) {
          // Cluster click – zobrazit seznam stanic na výběr; hintPos zůstane z posledního pointermove (hover nad clusterem)
          const clusterStations = feature.get('clusterStations') || []
          setHintStation({
            isClusterSelection: true,
            stations: clusterStations,
          })
          setClusterSelectionActive(true)
        } else {
          // Station click
          const station = feature.get('station')
          if (station) onStationSelectRef.current?.(station)
        }

        selectInteraction.getFeatures().clear()
      }
    })
    map.addInteraction(selectInteraction)

    // Hover – show hint panel (skip if cluster selection is active)
    map.on('pointermove', (e) => {
      if (clusterSelectionActiveRef.current) {
        map.getTargetElement().style.cursor = 'auto'
        return
      }
      if (!showHintRef.current) {
        setHintStation(null)
        return
      }
      const feature = map.forEachFeatureAtPixel(e.pixel, f => f, { hitTolerance: 12 })
      const isCluster = feature?.get('isCluster') ?? false
      let hintData = null

      if (isCluster) {
        const clusterStations = feature?.get('clusterStations') ?? []
        hintData = {
          isCluster: true,
          stations: clusterStations,
        }
      } else {
        hintData = feature?.get('station') ?? null
      }

      setHintStation(hintData)
      if (hintData) {
        const rect = mapRef.current.getBoundingClientRect()
        setHintPos({ x: e.originalEvent.clientX - rect.left, y: e.originalEvent.clientY - rect.top })
      }
      const isClickable = hintData !== null
      map.getTargetElement().style.cursor = isClickable ? 'pointer' : (localityPickModeRef.current ? 'crosshair' : '')
    })

    // Map click for locality filter (only when no feature was hit)
    map.on('singleclick', (e) => {
      const hit = map.hasFeatureAtPixel(e.pixel, { hitTolerance: 12 })
      if (!hit) {
        const coords = toLonLat(map.getCoordinateFromPixel(e.pixel))
        onMapClickRef.current?.({ lon: coords[0], lat: coords[1] })
      }
    })

    // Ulož mapu stav když se změní center nebo zoom
    const saveMapState = () => {
      const view = map.getView()
      const center = toLonLat(view.getCenter())
      const zoom = view.getZoom()
      setMapState({ center, zoom })
      setZoomLevel(zoom)
    }
    map.getView().on('change:center', saveMapState)
    map.getView().on('change:resolution', saveMapState)

    return () => {
      map.setTarget(null)
      mapInstanceRef.current = null
    }
  }, []) // eslint-disable-line

  // Update features when stations change or zoom level changes
  useEffect(() => {
    const source = vectorSourceRef.current
    const map = mapInstanceRef.current
    if (!source || !map) return
    source.clear()
    if (!stations || stations.length === 0) return

    const features = []

    const groups = groupStationsByProximity(stations, MAP_CONFIG_OFFSET.PROXIMITY_DISTANCE_METERS)

    if (zoomLevel < MAP_CONFIG_OFFSET.ZOOM_THRESHOLD) {
      // CLUSTERING MODE – clustery pro skupiny v okruhu 5m
      groups.forEach(group => {
        if (group.length === 0) return

        // Centroid skupiny
        const avgLon = group.reduce((sum, s) => sum + s.longitude, 0) / group.length
        const avgLat = group.reduce((sum, s) => sum + s.latitude, 0) / group.length

        if (group.length === 1) {
          // Jediná stanice – normální marker
          const s = group[0]
          const f = new Feature({
            geometry: new Point(fromLonLat([s.longitude, s.latitude])),
            ghId: s.gh_id,
            station: s,
            isCluster: false,
          })
          features.push(f)
        } else {
          // Více stanic v okruhu 5m – cluster
          const f = new Feature({
            geometry: new Point(fromLonLat([avgLon, avgLat])),
            clusterSize: group.length,
            clusterStations: group,
            isCluster: true,
          })
          features.push(f)
        }
      })
    } else {
      // OFFSET MODE – zobrazit s offsetem pro skupiny v okruhu 5m
      groups.forEach(group => {
        if (group.length === 0) return

        // Centroid skupiny
        const avgLon = group.reduce((sum, s) => sum + s.longitude, 0) / group.length
        const avgLat = group.reduce((sum, s) => sum + s.latitude, 0) / group.length

        group.forEach((s, index) => {
          const [offsetLon, offsetLat] = getOffsetCoordinates(
            avgLon,
            avgLat,
            index,
            group.length
          )

          const f = new Feature({
            geometry: new Point(fromLonLat([offsetLon, offsetLat])),
            ghId: s.gh_id,
            station: s,
            isCluster: false,
          })
          features.push(f)
        })
      })
    }

    source.addFeatures(features)
  }, [stations, zoomLevel])

  // Re-style when vizMode, labelMode, or selectedStation changes
  useEffect(() => {
    const layer = vectorLayerRef.current
    if (!layer) return
    // Popisky stanic jsou na nízkém zoomu (mapa celé ČR) příliš husté a překrývají se navzájem —
    // zobrazit je až od LABEL_ZOOM_THRESHOLD, body samotné zůstávají viditelné vždy.
    const effectiveLabelMode = zoomLevel < MAP_CONFIG_OFFSET.LABEL_ZOOM_THRESHOLD ? [] : labelMode
    layer.setStyle((feature) => {
      const isCluster = feature.get('isCluster')

      if (isCluster) {
        const clusterStations = feature.get('clusterStations') || []
        const obsColorMode = OBS_COLOR_MODES[vizMode]
        const radius = (obsColorMode || vizMode === 'type-shape' || vizMode === 'group') ? 9 : MAP_CONFIG_OFFSET.CLUSTER_CIRCLE_RADIUS
        const canvas = obsColorMode
          ? createClusterPieWithCount(getClusterElSlices(clusterStations, obsColorMode.getElements, obsColorMode.priority), clusterStations.length, radius)
          : vizMode === 'type-shape'
            ? createClusterPieWithCount(getClusterObsTypeSlices(clusterStations), clusterStations.length, radius)
            : vizMode === 'group'
              ? createClusterPieWithCount(getClusterGroupSlices(clusterStations, groupColorMap), clusterStations.length, radius)
              : createClusterPieChart(clusterStations, vizMode, typeColors, groupColorMap, radius)
        const iconUrl = canvas.toDataURL('image/png')
        const size = canvas.width

        const firstStation = clusterStations[0]
        const firstLabel = stationLabel(effectiveLabelMode, firstStation)
        const clusterLabel = firstLabel ? firstLabel + ',…' : null

        return new Style({
          image: new Icon({
            src: iconUrl,
            imgSize: [size, size],
            anchor: [0.5, 0.5],
          }),
          text: clusterLabel ? new Text({
            text: clusterLabel,
            offsetY: -(radius + 6),
            font: '10px sans-serif',
            fill: new Fill({ color: '#333' }),
            stroke: new Stroke({ color: '#fff', width: 2 }),
          }) : null,
        })
      }

      const isSelected = selectedStation && feature.get('ghId') === selectedStation.gh_id

      if (vizMode === 'group') {
        const station = feature.get('station')
        const colors = (groupColorMap && station && groupColorMap.get(station.gh_id)) || []

        // Stanice patřící jen do jedné (nebo žádné) skupiny → plný kruh jako dřív.
        if (colors.length <= 1) {
          const fillColor = colors[0] || '#2196f3'
          const radius = pointRadius ? (isSelected ? pointRadius.selected : pointRadius.normal) : (isSelected ? 9 : 6)
          return new Style({
            zIndex: isSelected ? 9999 : 0,
            image: new Circle({
              radius,
              fill: new Fill({ color: fillColor }),
              stroke: new Stroke({ color: isSelected ? '#fff' : 'rgba(255,255,255,0.8)', width: isSelected ? 3 : 1.5 }),
            }),
            text: makeLabelText(stationLabel(effectiveLabelMode, station), -12, labelStyle),
          })
        }

        // Stanice patřící do víc skupin naráz (překryv) → koláč jako u „Pozorování (koláč)“.
        // Klíčujeme indexem, ne barvou — dvě skupiny mohou mít stejnou barvu (paleta se opakuje po 15).
        const radius = pointRadius ? (isSelected ? pointRadius.selectedPie : pointRadius.normalPie) : (isSelected ? 12 : 9)
        const sliceKeys = colors.map((_, i) => i)
        const canvas = createPieChart(sliceKeys, colors, radius)
        const iconUrl = canvas.toDataURL('image/png')
        const size = canvas.width
        return new Style({
          zIndex: isSelected ? 9999 : 0,
          image: new Icon({ src: iconUrl, imgSize: [size, size], anchor: [0.5, 0.5] }),
          text: makeLabelText(stationLabel(effectiveLabelMode, station), -(radius + 6), labelStyle),
        })
      }

      if (OBS_COLOR_MODES[vizMode]) {
        const { getElements } = OBS_COLOR_MODES[vizMode]
        const station = feature.get('station')
        const els = getElements(station?.observation)
        const radius = isSelected ? 12 : 9
        const canvas = createPieChart(els, EL_COLORS, radius)
        const iconUrl = canvas.toDataURL('image/png')
        const size = canvas.width
        return new Style({
          zIndex: isSelected ? 9999 : 0,
          image: new Icon({ src: iconUrl, imgSize: [size, size], anchor: [0.5, 0.5] }),
          text: makeLabelText(stationLabel(effectiveLabelMode, station), -(radius + 6)),
        })
      }

      if (vizMode === 'type-shape') {
        const station = feature.get('station')
        const obsTypes = getObsTypes(station?.observation)
        const radius = isSelected ? 12 : 9
        const canvas = createPieChart(obsTypes, OBS_TYPE_COLORS, radius)
        const iconUrl = canvas.toDataURL('image/png')
        const size = canvas.width
        return new Style({
          zIndex: isSelected ? 9999 : 0,
          image: new Icon({ src: iconUrl, imgSize: [size, size], anchor: [0.5, 0.5] }),
          text: makeLabelText(stationLabel(effectiveLabelMode, station), -(radius + 6)),
        })
      }

      return makeFeatureStyle(vizMode, effectiveLabelMode, feature, isSelected, typeColors)
    })
    layer.changed()
  }, [vizMode, labelMode, selectedStation, typeColors, groupColorMap, pointRadius, labelStyle, zoomLevel])

  // Center on selected station
  useEffect(() => {
    if (!selectedStation || !mapInstanceRef.current) return
    if (selectedStation.latitude == null || selectedStation.longitude == null) return
    mapInstanceRef.current.getView().animate({
      center: fromLonLat([selectedStation.longitude, selectedStation.latitude]),
      duration: 500,
    })
  }, [selectedStation])

  const legend = vizMode === 'group'
    ? (groupLegend || [])
    : TYPE_COLOR_VARIANTS[vizMode] ? buildTypeLegend(typeColors) : (LEGEND[vizMode] || [])

  const vizModes = (groupColorMode || groupVizAvailable) ? [GROUP_VIZ_MODE, ...VIZ_MODES] : VIZ_MODES

  // Když zmizí filtr skupiny (groupVizAvailable → false) a uživatel byl přepnutý na „Podle skupiny“,
  // spadni zpět na výchozí vizualizaci — volba by jinak zůstala zvolená, ale chyběla by v seznamu.
  useEffect(() => {
    if (!groupColorMode && !groupVizAvailable && vizMode === 'group') {
      setVizMode(mapSettings.vizMode === 'group' ? 'elevation' : mapSettings.vizMode)
    }
  }, [groupColorMode, groupVizAvailable]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative w-full h-full">
      {/* Map container */}
      <div ref={mapRef} className="w-full h-full" />

      {/* Toolbar – left inside map */}
      {showToolbar && (
        <div className="absolute top-2 left-2 z-10 flex flex-col gap-2 bg-white/95 shadow-lg rounded-xl p-2 w-44 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-gray-600">Nástroje</span>
            <button
              className="text-gray-400 hover:text-gray-600 leading-none"
              onClick={() => setShowToolbar(false)}
              title="Skrýt nástroje"
            >✕</button>
          </div>
          <div>
            <div className="font-semibold mb-1 text-gray-600">Vizualizace</div>
            {vizModes.map(m => (
              <label key={m.value} className="flex items-center gap-1.5 cursor-pointer py-0.5">
                <input
                  type="radio"
                  name="vizMode"
                  value={m.value}
                  checked={vizMode === m.value}
                  onChange={() => setVizMode(m.value)}
                  className="accent-blue-500"
                />
                {m.label}
              </label>
            ))}
          </div>

          <div>
            <div className="font-semibold mb-1 text-gray-600">Popisek</div>
            {LABEL_MODES.map(m => (
              <label key={m.value} className="flex items-center gap-1.5 cursor-pointer py-0.5">
                <input
                  type="checkbox"
                  checked={labelMode.includes(m.value)}
                  onChange={() => toggleLabelMode(m.value)}
                  className="accent-blue-500"
                />
                {m.label}
              </label>
            ))}
          </div>

          <button
            className="text-blue-500 underline text-left mt-1"
            onClick={() => setShowLegend(v => !v)}
          >
            {showLegend ? 'Skrýt legendu' : 'Zobrazit legendu'}
          </button>

          <label className="flex items-center gap-1.5 cursor-pointer py-0.5 mt-1">
            <input
              type="checkbox"
              checked={showHint}
              onChange={e => setShowHint(e.target.checked)}
              className="accent-blue-500"
            />
            Popis při najetí
          </label>
        </div>
      )}

      {/* Legend (viditelná jen když je toolbar viditelný — nebo natvrdo na hideTools mapách, kde toolbar
          není k dispozici vůbec; tam je navíc v pravém horním rohu se zavírací ikonou, ne vlevo vedle toolbaru) */}
      {showLegend && (showToolbar || hideTools) && (
        <div className={`absolute z-10 bg-white/95 shadow-lg rounded-xl p-2 text-xs w-36 ${hideTools ? 'top-2 right-2' : 'top-2 left-48'}`}>
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold text-gray-600">Legenda</span>
            {hideTools && (
              <button
                className="text-gray-400 hover:text-gray-600 leading-none"
                onClick={() => setShowLegend(false)}
                title="Skrýt legendu"
              >✕</button>
            )}
          </div>
          {legend.map((item, i) => (
            <div key={i} className="flex items-center gap-1.5 py-0.5">
              <div className="w-3 h-3 rounded-full shrink-0" style={{ background: item.color }} />
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Tlačítko pro znovuzobrazení legendy na hideTools mapách (nemají toolbar, odkud by šla zapnout) */}
      {!showLegend && hideTools && (
        <button
          className="absolute top-2 right-2 z-10 flex items-center justify-center w-9 h-9 bg-white/95 shadow-md rounded-xl text-base text-gray-600 hover:bg-gray-50 border border-gray-200 transition-colors"
          onClick={() => setShowLegend(true)}
          title="Zobrazit legendu"
        >🎨</button>
      )}

      {/* Hint panel on hover */}
      {showHint && hintStation && (
        <div
          ref={hintPanelRef}
          className="absolute z-20"
          style={{ left: hintPos.x + 12, top: hintPos.y + 12 }}
        >
          <StationPanelHint
            station={hintStation}
            onStationSelect={(station) => {
              setClusterSelectionActive(false)
              setHintStation(null)
              onStationSelectRef.current?.(station)
            }}
            onClose={() => {
              setClusterSelectionActive(false)
              setHintStation(null)
            }}
            onZoom={() => {
              const stations = hintStation?.stations ?? []
              if (stations.length === 0 || !mapInstanceRef.current) return
              const avgLon = stations.reduce((s, st) => s + st.longitude, 0) / stations.length
              const avgLat = stations.reduce((s, st) => s + st.latitude, 0) / stations.length
              mapInstanceRef.current.getView().animate({
                center: fromLonLat([avgLon, avgLat]),
                zoom: MAP_CONFIG_OFFSET.ZOOM_THRESHOLD + 1,
                duration: MAP_CONFIG_OFFSET.CLUSTER_ANIMATION_DURATION,
              })
              setClusterSelectionActive(false)
              setHintStation(null)
            }}
          />
        </div>
      )}

      {/* Tlačítko pro zobrazení toolbaru když je skrytý (ne na hideTools mapách — tam nástroje nejsou k dispozici vůbec) */}
      {!showToolbar && !hideTools && (
        <button
          className="absolute top-2 left-2 z-10 bg-white/95 shadow-lg rounded-xl px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
          onClick={() => setShowToolbar(true)}
          title="Zobrazit nástroje"
        >▶ Nástroje</button>
      )}
    </div>
  )
}
